import { useEffect, useState } from 'react';
import { BufferCountModal } from './BufferCountModal.tsx';
import { BufferStoragePath, type BufferStorageCell } from './BufferStoragePath.tsx';
import { Link } from 'react-router';
import { Alert, Badge, Button, Drawer, Group, Loader, Pagination, Paper, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { qs } from '../api/client.ts';
import { useApiQuery, useDebounced, useWarehouse, useAuth } from '../lib/hooks.ts';
import { fmtQty } from '../lib/format.ts';

interface BufferItem {
  product_id: number; warehouse_id: number; sku: string; article_1c: string | null;
  name: string; unit: string; is_kit: boolean; free: number; stock_buffer: number;
  to_marketplace: number; warehouse_name: string;
  storage_address?: string | null; storage_cells?: BufferStorageCell[];
  affected_kits: number; direct_blocked: boolean;
  marketplaces: { platform: 'wb' | 'ozon'; account_name: string }[];
}
interface BufferResult { total: number; items: BufferItem[] }
const PAGE_SIZE = 30;

/** Постоянный индикатор: просмотр не скрывает проблему, пополнение снимает её. */
export function BufferAlerts() {
  const warehouseId = useWarehouse();
  const { user } = useAuth();
  const [counting, setCounting] = useState<BufferItem | null>(null);
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const query = useDebounced(search);
  const summary = useApiQuery<BufferResult>(['buffer-alerts', 'count', warehouseId],
    warehouseId ? `/stocks/buffer-alerts${qs({ warehouse_id: warehouseId, limit: 1 })}` : null,
    { refetchInterval: 60_000 });
  const list = useApiQuery<BufferResult>(['buffer-alerts', 'list', warehouseId, query, page],
    opened && warehouseId ? `/stocks/buffer-alerts${qs({ warehouse_id: warehouseId, search: query || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE })}` : null,
    { refetchInterval: 60_000 });
  useEffect(() => { setPage(1); }, [warehouseId, query]);
  const pages = Math.max(1, Math.ceil((list.data?.total ?? 0) / PAGE_SIZE));
  useEffect(() => { if (list.data && page > pages) setPage(pages); }, [list.data, page, pages]);
  const count = summary.data?.total;
  const failed = summary.isError;
  const label = !warehouseId ? 'Выберите склад для проверки буфера'
    : failed ? 'Не удалось проверить товары в буфере'
    : count == null ? 'Проверяем товары в буфере'
    : `Товары в буфере: ${count}`;
  return <>
    <Tooltip label={label}>
      <Button variant="light" color={failed ? 'red' : count ? 'orange' : 'gray'} size="compact-sm"
        disabled={!warehouseId} aria-label={label} aria-haspopup="dialog" onClick={() => setOpened(true)}>
        <Group gap={5} wrap="nowrap">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
          </svg>
          <Text span size="xs" visibleFrom="sm">Буфер</Text>
          <Badge size="xs" color={failed ? 'red' : count ? 'orange' : 'gray'} variant="filled">
            {failed ? '!' : count == null ? '…' : count > 99 ? '99+' : count}
          </Badge>
        </Group>
      </Button>
    </Tooltip>
    <Drawer opened={opened} onClose={() => setOpened(false)} position="right" size="lg" title="Товары, чей буфер блокирует продажи на МП">
      <Stack gap="sm">
        <Alert color="orange">
          Показаны исходные товары, из-за буфера которых не продаются комплекты или сам товар. Один товар — одна строка.
          <Text size="xs" mt={5}>Показан расчёт WMS для выбранного склада, а не проверка карточек в кабинетах WB и Ozon. После пополнения или изменения буфера список обновится автоматически.</Text>
        </Alert>
        <Group justify="space-between">
          <Text size="sm" fw={600}>{list.data ? `Найдено: ${list.data.total}` : 'Проверяем остатки…'}</Text>
          <Button size="compact-xs" variant="subtle" loading={list.isFetching} onClick={() => { void summary.refetch(); void list.refetch(); }}>Обновить</Button>
        </Group>
        <TextInput aria-label="Поиск товаров в буфере" placeholder="Название или артикул" value={search}
          onChange={e => { setSearch(e.currentTarget.value); setPage(1); }} />
        {list.isError ? <Alert color="red">Не удалось проверить буфер. Данные могут быть устаревшими. Нажмите «Обновить».</Alert>
          : list.isLoading ? <Group justify="center" p="md"><Loader size="sm" /><Text size="sm">Проверяем остатки…</Text></Group>
          : list.data?.total === 0 ? <Text c="dimmed" size="sm">{query ? 'По вашему запросу товаров в буфере нет.' : 'Товаров, чей буфер блокирует продажи на МП, нет.'}</Text>
          : list.data?.items.map(item => <Paper key={`${item.product_id}-${item.warehouse_id}`} withBorder p="sm" radius="md">
            <Stack gap={5}>
              <Text fw={600} size="sm">{item.name}</Text>
              <Text size="xs" c="dimmed">{item.article_1c || item.sku} · {item.warehouse_name}</Text>
              <BufferStoragePath assigned={item.storage_address} cells={item.storage_cells} unit={item.unit} />
              <Group gap={5}>{item.marketplaces.map(m => <Badge key={`${m.platform}-${m.account_name}`} size="xs" variant="light" color={m.platform === 'wb' ? 'grape' : 'blue'}>{m.platform === 'wb' ? 'WB' : 'Ozon'} · {m.account_name}</Badge>)}</Group>
              <Text size="sm">Свободно: <b>{fmtQty(item.free, item.unit)}</b> · Буфер: <b>{fmtQty(item.stock_buffer, item.unit)}</b> · Сверх буфера: <b>{fmtQty(item.to_marketplace, item.unit)}</b></Text>
              {item.affected_kits>0 && <Text size="sm" c="orange.9" fw={600}>Из-за буфера не продаются комплекты: {item.affected_kits}</Text>}
              {item.affected_kits>0 && <Text size="xs" c="dimmed">Остатка сверх буфера не хватает на комплекты с учётом их состава и собственного буфера.</Text>}
              {item.direct_blocked && <Text size="sm" c="orange.9">Сам товар: к публикации на МП — 0.</Text>}
              {item.unit === 'г' && (user?.role === 'admin' || user?.role === 'manager') && <Button size="compact-xs" onClick={() => setCounting(item)} style={{ alignSelf: 'flex-start' }}>Сверить фактический вес</Button>}
              <Button component={Link} to={`/stocks${qs({ search: item.sku, tab: 'goods' })}`}
                onClick={() => setOpened(false)} variant="light" size="compact-xs" style={{ alignSelf: 'flex-start' }}>Открыть остатки товара</Button>
            </Stack>
          </Paper>)}
        {list.data && pages > 1 && <Pagination total={pages} value={page} onChange={setPage} size="sm" siblings={1} />}
      </Stack>
    </Drawer>
    {counting && <BufferCountModal key={`${counting.product_id}-${counting.warehouse_id}`} productId={counting.product_id} warehouseId={counting.warehouse_id} onClose={() => { setCounting(null); void summary.refetch(); void list.refetch(); }} />}
  </>;
}
